import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import styled from 'styled-components';
import { ROUTER_CATALOG_DETAILS } from 'app/AppRouter';
import {
  ApplicabilityIcon,
  ArrowClockIcon,
  ArrowHeightIcon,
  ArrowWidthIcon,
  BoltIcon,
  CalendarAltIcon,
  CarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EngineIcon,
  ExchangeAltIcon,
  FileSearchIcon,
  GasIcon,
  GearboxIcon,
  TrashAltIcon,
} from 'assets/icons';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton';
import { Dialog, useDisclosure } from 'components/Dialog';
import Popover from 'components/Popover';
import { VehicleBanner } from 'components/VehicleBanner';
import PrivateComponent from 'composers/PrivateComponent';
import { fetchVehicleTechnicalCriteriaRequestSaga, getLastTechnicalCriteria } from 'domains/catalog/Catalog.store';
import { GEARBOX_TYPE, getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { fetchClearVehicleSearchHistoryRequestSaga, getVehicleSearchHistory } from 'domains/user';
import { IdentifyNewVehicleContent } from 'pages/CatalogPage/common/CatalogBanner/IdentifyNewVehicleContent';
import { CarDetailHeaderImage } from 'pages/CatalogPage/common/CatalogHeader/CatalogHeader';
import SearchHistoryContent from 'pages/CatalogPage/DH/PopoverContent';
import { useGetCriteria } from 'pages/CatalogPage/DH/VehicleTechnicalDetails/TechnicalCriteria';
import { theme } from 'styles';
import {
  Box,
  capitalize,
  CenterFlex,
  Flex,
  Icon,
  IconButton,
  IconType,
  Link,
  MarginBox,
  Text,
  WithLink,
  WithTooltip,
} from 'UI';
import { getData, textFormatter } from 'utils';
import { useSmall } from 'utils/hooks/useBreakpoint';
import { useResetScrollSharp } from 'utils/hooks/useResetScroll';
import { Country } from 'utils/i18n/Country';
import { formatVehicleBrand } from 'utils/mappers';

const VEHICLE_ACTION_ICON_SIZE = 22;
const SECTION_MARGIN_COMPACT = 12;
const SECTION_MARGIN_DESKTOP = 24;
const MISSING_VALUE_PLACEHOLDER = '-';
const FLEX_JUSTIFY_CENTER = 'center';
const ZERO_FLEX_SIZE = 0;
const FLEX_START = 'flex-start';
const UTILITY_VEHICLE_WHEELBASE_TECHNICAL_CRITERIA_CODE = '447';

const isDataHubUtilityVehicle = (result: VehicleDetail): boolean =>
  Boolean(result.dataHubVehicle?.modelTypeCode?.startsWith('F'));

const formatPowerLabel = ({
  kwValue,
  hpValue,
  t,
}: {
  kwValue?: number | string;
  hpValue?: number | string;
  t: (key: string, defaultValue: string, options?: Record<string, number | string>) => string;
}): string => {
  const kwLabel = kwValue ? t('catalog.parts.banner.kw', '{{kw}} kW', { kw: kwValue }) : '';
  const hpLabel = hpValue ? t('catalog.parts.banner.hp', '{{hp}} HP', { hp: hpValue }) : '';

  if (kwLabel && hpLabel) {
    return `${kwLabel} / ${hpLabel}`;
  }
  return kwLabel || hpLabel || MISSING_VALUE_PLACEHOLDER;
};

const formatFuelLabel = (value?: string): string =>
  value ? capitalize(value.toLowerCase()) : MISSING_VALUE_PLACEHOLDER;

const getHpFromKw = (kwValue?: string): number | undefined => {
  if (!kwValue) {
    return undefined;
  }
  const numericKw = Number(kwValue);
  return Number.isFinite(numericKw) ? Math.floor(1.341 * numericKw) : undefined;
};

export const Label = styled.div<{ isCompact?: boolean }>`
  max-width: ${({ isCompact }) => (isCompact ? '240px' : '300px')};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SectionItem = styled(Flex)<{ $isCompact?: boolean }>`
  min-width: fit-content;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
  margin-right: ${({ $isCompact }) => ($isCompact ? SECTION_MARGIN_COMPACT : SECTION_MARGIN_DESKTOP)}px;
  margin-bottom: ${({ $isCompact }) => ($isCompact ? 8 : 0)}px;
`;

const SectionWrapper = ({ children, isCompact }: Readonly<{ children: React.ReactNode; isCompact?: boolean }>) => {
  return (
    <SectionItem size={ZERO_FLEX_SIZE} $isCompact={isCompact}>
      {children}
    </SectionItem>
  );
};

const CatalogExpandedDetailsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const CatalogExpandedRow = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 24px;
  width: 100%;
`;

type TechnicalFieldKey = 'engine' | 'power' | 'fuel' | 'gearbox';

const SFlex = styled(Flex)`
  width: 100%;
  border: 1px solid ${({ theme: styledTheme }) => styledTheme.color.grey85};
  border-radius: 0 12px 12px 12px;
  box-shadow: 0 8px 20px -16px rgba(0, 0, 0, 0.4);
  overflow: hidden;
`;

const ActionButtonWrapper = styled.div`
  display: flex;
`;

const CatalogButtonsRow = styled(Flex)`
  gap: 8px;
  align-items: center;
`;

const IconActionButton = styled.button`
  border: none;
  background: ${({ theme: styledTheme }) => styledTheme.color.grey95};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${({ theme: styledTheme }) => styledTheme.color.grey75};
  }
`;

const SHistoryTitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
`;

const SHistoryTitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
`;

export const SHistoryTitleText = styled.span`
  font-family: NouvelR, sans-serif;
  font-size: 18px;
  font-weight: bold;
  color: white;
  letter-spacing: -0.12px;
`;

const SVehicleCountBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 1px 8px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

const SClearAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  font-size: 13px;
  padding: 0;
  font-weight: 500;
  margin-right: 12px;
  opacity: 0.85;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

export const SearchHistoryPopoverTitle = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const vehicleCount = useSelector(getVehicleSearchHistory).data?.length ?? 0;
  const disclosure = useDisclosure();

  const handleConfirm = useCallback(() => {
    dispatch(fetchClearVehicleSearchHistoryRequestSaga());
  }, [dispatch]);

  return (
    <>
      <SHistoryTitleBar>
        <SHistoryTitleLeft>
          <SHistoryTitleText>{t('catalog.history', 'Search History')}</SHistoryTitleText>
          {vehicleCount > 0 && (
            <SVehicleCountBadge>
              {vehicleCount} {t('catalog.history.vehicles_count', 'vehicles')}
            </SVehicleCountBadge>
          )}
        </SHistoryTitleLeft>
        <SClearAllButton type={'button'} onClick={disclosure.onOpen}>
          {t('catalog.history.clear_all', 'Clear all')}
        </SClearAllButton>
      </SHistoryTitleBar>
      <Dialog
        disclosure={disclosure}
        title={t('catalog.history.dialog.delete_history', 'Delete history')}
        description={[
          t(
            'catalog.history.dialog.delete_history.description',
            'This action will delete your entire car search history.',
          ),
          t('common.dialog.confirmationQuestion', 'Would you like to confirm?'),
        ]}
        icon={TrashAltIcon}
        status={'info'}
        handleConfirm={handleConfirm}
      />
    </>
  );
};

export const CatalogButton = ({
  id,
  icon,
  title,
  link,
  external,
  content,
  contentTitle,
  isPopoverVisible,
  onPopoverVisibleChange,
  tooltip,
  noMargin: _noMargin,
  boxSize = 'lg',
  'data-cy': dataCy,
}: {
  id: string;
  icon: IconType;
  title?: string;
  link?: string;
  external?: boolean;
  content: React.ReactElement;
  contentTitle?: React.ReactNode;
  isPopoverVisible?: boolean;
  onPopoverVisibleChange?: (visible: boolean) => void;
  tooltip?: string;
  noMargin?: boolean;
  boxSize?: 'xsm' | 'sm' | 'smd' | 'md' | 'lg' | 'xl';
  'data-cy'?: string;
}) => {
  const location = useLocation();
  const renderButton = link ? location.pathname !== link : true;
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    isPopoverVisible !== undefined && setIsVisible(isPopoverVisible);
  }, [isPopoverVisible]);

  if (!renderButton) return null;

  const CatalogIconButton = () => (
    <WithTooltip title={title} placement={'bottom'}>
      <button type={'button'} aria-label={title} style={{ border: 'none', background: 'transparent', padding: 0 }}>
        <IconButton
          IconComponent={icon}
          round
          size={VEHICLE_ACTION_ICON_SIZE}
          boxSize={boxSize}
          color={'grey15'}
          bgColor={'grey95'}
          hoverFill={'grey75'}
          data-cy={dataCy}
        />
      </button>
    </WithTooltip>
  );

  const WrappedButton = () =>
    tooltip ? (
      <WithTooltip title={tooltip}>
        <Flex align={'center'} justify={'center'}>
          <CatalogIconButton />
        </Flex>
      </WithTooltip>
    ) : (
      <CatalogIconButton />
    );

  return (
    <ActionButtonWrapper>
      {link ? (
        <Link to={link} external={external} newTab={external}>
          <WrappedButton />
        </Link>
      ) : (
        <Popover
          id={id}
          visible={isVisible}
          onVisibleChange={(visible: boolean) => {
            setIsVisible(visible);
            onPopoverVisibleChange && onPopoverVisibleChange(visible);
          }}
          trigger={'click'}
          title={contentTitle}
          content={content}
          placement={'bottomRight'}
          arrowPointAtCenter={true}
          arrowColor={theme.color.brand_black}
        >
          <WrappedButton />
        </Popover>
      )}
    </ActionButtonWrapper>
  );
};

export const ChangeVehiclePopup = ({
  visible,
  setIsChangeVehiclePopupVisible,
  // todo: onFound has to be passed with useCallback, same as in EstimateModifyTabButtonsSection,
  //  otherwise useEffect in SearchByVINVRN will be stuck
  onFound,
  setIsChangeVehicle,
}: {
  visible?: boolean;
  setIsChangeVehiclePopupVisible: (a: boolean) => void;
  onFound?: (vehicleDetail: VehicleDetail) => void;
  setIsChangeVehicle?: (b: boolean) => void;
}) => (
  <IdentifyNewVehicleContent
    visible={visible}
    setIsChangeVehiclePopupVisible={setIsChangeVehiclePopupVisible}
    onFound={onFound}
    setIsChangeVehicle={setIsChangeVehicle}
  />
);

export const getVehicleTitle = (
  result: VehicleDetail,
  isBMM: boolean,
  t: (key: string, defaultValue: string, options?: Record<string, number | string>) => string,
): string =>
  [
    formatVehicleBrand(result.vehicleBrand),
    result.model,
    result.iamVehicle &&
      !isBMM &&
      result.iamVehicle.phase &&
      t('catalog.parts.banner.phase', 'Phase {{phase}}', { phase: result.iamVehicle.phase }),
  ]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();

export const getVehicleBodyTypeLabel = (result: VehicleDetail): string | undefined => {
  if (result.dataHubVehicle?.modelType) {
    return result.dataHubVehicle.modelType;
  }
  if (result.iamVehicle?.bodyType) {
    return capitalize(result.iamVehicle.bodyType.toLowerCase());
  }
  return undefined;
};

export const CatalogButtons = ({
  query,
  setIsChangeVehicle,
  showExpandToggle,
  isShowFull,
  setIsShowFull,
}: {
  query: string;
  setIsChangeVehicle?: (b: boolean) => void;
  showExpandToggle?: boolean;
  isShowFull?: boolean;
  setIsShowFull?: (v: boolean) => void;
}) => {
  const { t } = useTranslation();
  const isSmall = useSmall();
  const [isChangeVehiclePopupVisible, setIsChangeVehiclePopupVisible] = useState<boolean>(false);
  const FR_INSPECTION_CHECKLIST_URL =
    'https://s3.eu-west-1.amazonaws.com/provider.medias.parts-io.com/docs/pdfs/TECHNICALCONTROL_FR_20250317.pdf';

  useEffect(() => {
    setIsChangeVehiclePopupVisible(false);
  }, [query]);

  function onToggleExpand() {
    if (setIsShowFull) {
      setIsShowFull(!isShowFull);
    }
  }

  const expandToggleButtonProps = { type: 'button' as const, onClick: onToggleExpand };

  return (
    <CatalogButtonsRow direction={'row'} justify={'flex-end'} size={ZERO_FLEX_SIZE}>
      <CatalogButton
        id={'change_vehicle_button_' + query}
        title={t('catalog.parts.action.identify_new_vehicle', 'Identify New Vehicle')}
        icon={ExchangeAltIcon}
        data-cy={'change-vehicle-button'}
        boxSize={'smd'}
        content={
          <ChangeVehiclePopup
            visible={isChangeVehiclePopupVisible}
            setIsChangeVehiclePopupVisible={setIsChangeVehiclePopupVisible}
            setIsChangeVehicle={setIsChangeVehicle}
          />
        }
        contentTitle={
          <SHistoryTitleText>
            {t('catalog.parts.action.identify_new_vehicle', 'Identify New Vehicle')}
          </SHistoryTitleText>
        }
        isPopoverVisible={isChangeVehiclePopupVisible}
        onPopoverVisibleChange={(visible: boolean) => setIsChangeVehiclePopupVisible(visible)}
      />
      <PrivateComponent
        requiredCountries={[Country.FR]}
        render={() => (
          <CatalogButton
            id={'show_inspection_checklist_button_' + query}
            title={t('catalog.parts.action.inspection_checklist.tooltip', 'See technical inspection checklist')}
            icon={FileSearchIcon}
            link={FR_INSPECTION_CHECKLIST_URL}
            external
            content={<></>}
            boxSize={'smd'}
            data-cy={'inspection-checklist-button'}
          />
        )}
      />
      <CatalogButton
        id={'show_vehicle_history_button_' + query}
        title={t('catalog.parts.action.search_history', 'Search history')}
        icon={ArrowClockIcon}
        content={<SearchHistoryContent />}
        contentTitle={<SearchHistoryPopoverTitle />}
        boxSize={'smd'}
        data-cy={'search-history-button'}
      />
      {showExpandToggle && (
        <div style={{ display: 'flex' }}>
          <WithTooltip
            title={isShowFull ? t('common.action.show_less', 'Show less') : t('common.action.show_more', 'Show more')}
            placement={'bottom'}
          >
            <div>
              <IconActionButton {...expandToggleButtonProps} data-cy={'catalog-vehicle-expand-toggle'}>
                <Icon
                  IconComponent={isShowFull ? ChevronUpIcon : ChevronDownIcon}
                  size={isSmall ? 32 : 22}
                  color={'grey15'}
                />
              </IconActionButton>
            </div>
          </WithTooltip>
        </div>
      )}
    </CatalogButtonsRow>
  );
};

export const VehicleTabTitle = ({
  result,
  isBMM,
  link,
  countryName,
  inverted,
  disableEllipsis,
}: {
  result: VehicleDetail;
  isBMM: boolean;
  link?: string;
  countryName: string | undefined;
  inverted?: boolean;
  disableEllipsis?: boolean;
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const isSmall = useSmall();
  const country = Country.findByKey(countryName);
  const textType = inverted ? 'lead_text_white' : 'lead_text';

  const vehicleTitle = getVehicleTitle(result, isBMM, t);

  if (isSmall) {
    return (
      <Flex direction={'column'} align={FLEX_START} justify={FLEX_JUSTIFY_CENTER} size={ZERO_FLEX_SIZE}>
        <WithLink to={link}>
          <Flex align={'center'} gap={8}>
            <Text type={textType} data-cy={'vehicle-name'}>
              {vehicleTitle}
            </Text>
          </Flex>
        </WithLink>
        <MarginBox mt={6} />
        <Flex align={'center'} wrap={'wrap'} gap={10}>
          {!isBMM && country && (
            <Icon
              IconComponent={country.icon}
              width={16}
              height={16}
              round
              onClick={() => link && history.push(link)}
              data-cy={'country'}
            />
          )}
          {result.vrn && (
            <Flex align={'center'} gap={10} justify={FLEX_JUSTIFY_CENTER}>
              <div data-cy={'vrn'}>
                <Text type={textType} disableGutter>
                  {result.vrn}
                </Text>
              </div>
              <CenterFlex>
                <CopyToClipboardButton textToCopy={result.vrn} size={22} inverted={inverted} />
              </CenterFlex>
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex align={'center'} wrap={'nowrap'} gap={16} size={ZERO_FLEX_SIZE}>
      <WithLink to={link}>
        <Flex align={'center'} gap={8} minWidth={'0'} size={disableEllipsis ? '0 0 auto' : ZERO_FLEX_SIZE}>
          <Text type={textType} noWrap={!disableEllipsis} data-cy={'vehicle-name'} ellipsis={!disableEllipsis}>
            {vehicleTitle}
          </Text>
        </Flex>
      </WithLink>
      <Flex align={'center'} gap={12} justify={'center'} size={ZERO_FLEX_SIZE}>
        {!isBMM && country && (
          <Icon
            IconComponent={country.icon}
            width={16}
            height={16}
            round
            onClick={() => link && history.push(link)}
            data-cy={'country'}
          />
        )}
        {result.vrn && (
          <Flex align={'center'} gap={10} justify={FLEX_JUSTIFY_CENTER} size={ZERO_FLEX_SIZE}>
            <div data-cy={'vrn'}>
              <Text type={textType} disableGutter noWrap>
                {result.vrn}
              </Text>
            </div>
            <CenterFlex>
              <CopyToClipboardButton textToCopy={result.vrn} size={22} inverted={inverted} />
            </CenterFlex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export const VehicleTabTitleCompact = ({
  result,
  isBMM,
  inverted,
}: {
  result: VehicleDetail;
  isBMM: boolean;
  inverted?: boolean;
}) => {
  const { t } = useTranslation();
  const textType = inverted ? 'lead_text_white' : 'lead_text';

  return (
    <Text type={textType} noWrap ellipsis data-cy={'vehicle-name-compact'}>
      {getVehicleTitle(result, isBMM, t)}
    </Text>
  );
};

export const VehicleExpandedDetails = ({
  result,
  isBMM,
  layout = 'default',
}: {
  result: VehicleDetail;
  isBMM: boolean;
  layout?: 'default' | 'compact';
}) => {
  const hpPowerIAM = result.iamVehicle?.hpPower ?? getHpFromKw(result.iamVehicle?.kwPower);
  const hpPowerDHKWsub = result.dataHubVehicle?.powerKw?.substring(0, 3);
  const hpPowerDHKW = Number.isNaN(Number(hpPowerDHKWsub)) ? hpPowerDHKWsub : Number(hpPowerDHKWsub);
  const hpPowerDHHP = typeof hpPowerDHKW === 'number' && Math.floor(1.341 * hpPowerDHKW);
  const isIAM = result.catalogSource === 'IAM';

  const isVan = isDataHubUtilityVehicle(result);
  const toDate =
    result.dataHubVehicle === undefined && result.iamVehicle?.toDate ? ` - ${result.iamVehicle.toDate}` : '';

  const getIamDateLabel = () => {
    if (!result.iamVehicle) {
      return '';
    }
    if (result.iamVehicle.registrationDate) {
      return result.iamVehicle.registrationDate;
    }
    if (result.iamVehicle.fromDate) {
      return `${result.iamVehicle.fromDate}${toDate}`;
    }
    return '';
  };

  const dateField = (result.manufacturingDate ||
    result.iamVehicle?.registrationDate ||
    result.iamVehicle?.toDate ||
    result.iamVehicle?.fromDate) && (
    <SectionWrapper isCompact>
      <Icon IconComponent={CalendarAltIcon} height={16} width={16} display={'flex'} noPointer />
      <Text type={'text'} data-cy={'manufacture-date-expanded'}>
        {result.dataHubVehicle &&
          result.manufacturingDate &&
          textFormatter.formatDate(new Date(result.manufacturingDate))}
        {!result.dataHubVehicle && result.iamVehicle && getIamDateLabel()}
      </Text>
    </SectionWrapper>
  );

  const powerFields = (
    <>
      {!isIAM && (
        <DHField result={result} isBMM={isBMM} hpPowerDHKW={hpPowerDHKW} hpPowerDHHP={hpPowerDHHP} isCompact />
      )}
      {isIAM && <OtherField result={result} hpPowerIAM={hpPowerIAM} isCompact />}
    </>
  );

  const trailingFields = (
    <>
      {isVan && <RoofHeightField result={result} isCompact />}
      <VehicleStatus result={result} />
    </>
  );

  if (layout === 'compact') {
    return (
      <CatalogExpandedDetailsGrid data-cy={'catalog-vehicle-expanded-grid'}>
        <CatalogExpandedRow>
          {dateField}
          {isVan && <WheelBaseLengthField result={result} isCompact />}
        </CatalogExpandedRow>
        <CatalogExpandedRow>
          {!isIAM && (
            <DHField
              result={result}
              isBMM={isBMM}
              hpPowerDHKW={hpPowerDHKW}
              hpPowerDHHP={hpPowerDHHP}
              isCompact
              fieldsToShow={['engine', 'power', 'fuel']}
            />
          )}
          {isIAM && (
            <OtherField result={result} hpPowerIAM={hpPowerIAM} isCompact fieldsToShow={['engine', 'power', 'fuel']} />
          )}
        </CatalogExpandedRow>
        <CatalogExpandedRow>
          {!isIAM && (
            <DHField
              result={result}
              isBMM={isBMM}
              hpPowerDHKW={hpPowerDHKW}
              hpPowerDHHP={hpPowerDHHP}
              isCompact
              fieldsToShow={['gearbox']}
            />
          )}
          {isIAM && <GearboxEnergyField result={result} isCompact fieldsToShow={['gearbox']} />}
        </CatalogExpandedRow>
      </CatalogExpandedDetailsGrid>
    );
  }

  return (
    <Flex align={'center'} wrap={'wrap'} gap={'8px 24px'}>
      {powerFields}
      {dateField}
      {isVan && <WheelBaseLengthField result={result} isCompact />}
      {trailingFields}
    </Flex>
  );
};

const VINField = ({ result, isBMM, isCompact }: { result: VehicleDetail; isBMM: boolean; isCompact?: boolean }) => {
  const { t } = useTranslation();
  return (
    <>
      {!isBMM && result.vin && (
        <SectionWrapper isCompact={isCompact}>
          <Text type="base" bold data-cy={'vin'}>
            {t('catalog.parts.banner.vin', 'VIN: {{vin}}', { vin: result.vin })}
          </Text>
          <MarginBox mr={4} />
          <CopyToClipboardButton textToCopy={result.vin} />
        </SectionWrapper>
      )}
    </>
  );
};

export const IAMField = ({ result, isCompact }: { result: VehicleDetail; isCompact?: boolean }) => {
  const { t } = useTranslation();
  const toDate =
    result.dataHubVehicle === undefined && result.iamVehicle?.toDate ? ` - ${result.iamVehicle.toDate}` : '';
  const isVan = isDataHubUtilityVehicle(result);

  return (
    <>
      {result.dataHubVehicle && result.dataHubVehicle.modelType && (
        <SectionWrapper isCompact={isCompact}>
          <Icon IconComponent={CarIcon} height={16} width={16} display={'flex'} noPointer />
          <Text type={'text'} data-cy={'model-type'}>
            {result.dataHubVehicle?.modelType && result.dataHubVehicle.modelType}
          </Text>
        </SectionWrapper>
      )}
      {result.dataHubVehicle === undefined &&
        result.iamVehicle &&
        (result.iamVehicle.doors || result.iamVehicle.bodyType) && (
          <SectionWrapper isCompact={isCompact}>
            <Icon IconComponent={CarIcon} height={16} width={16} display={'flex'} noPointer />
            <Text type={'text'} data-cy={'doors-body-type'}>
              {result.iamVehicle?.bodyType && capitalize(result.iamVehicle.bodyType.toLowerCase())}
              {result.iamVehicle.doors &&
                ` ${t('catalog.parts.banner.doors', '{{doors}} doors', { doors: result.iamVehicle.doors })}`}
            </Text>
          </SectionWrapper>
        )}
      {(result.manufacturingDate ||
        result.iamVehicle?.registrationDate ||
        result.iamVehicle?.toDate ||
        result.iamVehicle?.fromDate) && (
        <SectionWrapper isCompact={isCompact}>
          <Icon IconComponent={CalendarAltIcon} height={16} width={16} display={'flex'} noPointer />
          <Text type={'text'} data-cy={'manufacture-date'}>
            {result.dataHubVehicle &&
              result.manufacturingDate &&
              textFormatter.formatDate(new Date(result.manufacturingDate))}
            {result.dataHubVehicle === undefined &&
              result.iamVehicle &&
              (result.iamVehicle.registrationDate
                ? result.iamVehicle.registrationDate
                : result.iamVehicle.fromDate
                ? `${result.iamVehicle.fromDate}${toDate}`
                : '')}
          </Text>
        </SectionWrapper>
      )}
      {isVan && (
        <>
          <WheelBaseLengthField result={result} isCompact={isCompact} />
          <RoofHeightField result={result} isCompact={isCompact} />
        </>
      )}
    </>
  );
};

const shouldShowTechnicalField = (fieldsToShow: TechnicalFieldKey[] | undefined, field: TechnicalFieldKey) =>
  !fieldsToShow || fieldsToShow.includes(field);

const DHField = ({
  result,
  isBMM,
  hpPowerDHKW,
  hpPowerDHHP,
  isCompact,
  fieldsToShow,
}: {
  result: VehicleDetail;
  isBMM: boolean;
  hpPowerDHKW: number | string | undefined;
  hpPowerDHHP: number | false;
  isCompact?: boolean;
  fieldsToShow?: TechnicalFieldKey[];
}) => {
  const { t } = useTranslation();
  const gearbox = result.dataHubVehicle?.gearbox ? capitalize(result.dataHubVehicle.gearbox.toLowerCase()) : undefined;
  const powerLabel = formatPowerLabel({
    kwValue: hpPowerDHKW,
    hpValue: hpPowerDHHP || undefined,
    t,
  });
  const fuelLabel = formatFuelLabel(result.dataHubVehicle?.energyType);

  return (
    <>
      {result.dataHubVehicle && (
        <>
          {shouldShowTechnicalField(fieldsToShow, 'engine') && result.dataHubVehicle.engine && (
            <SectionWrapper isCompact={isCompact}>
              <Icon IconComponent={EngineIcon} height={16} width={16} display={'flex'} noPointer />
              <Text type={'text'} data-cy={'engine-type'}>{`${result.dataHubVehicle.engine}`}</Text>
            </SectionWrapper>
          )}
          {shouldShowTechnicalField(fieldsToShow, 'power') && !isBMM && (
            <SectionWrapper isCompact={isCompact}>
              <Icon IconComponent={BoltIcon} height={16} width={16} display={'flex'} noPointer />
              <Text type={'text'} data-cy={'energy-type'}>
                {powerLabel}
              </Text>
            </SectionWrapper>
          )}
          {shouldShowTechnicalField(fieldsToShow, 'fuel') && (
            <SectionWrapper isCompact={isCompact}>
              <Icon IconComponent={GasIcon} height={16} width={16} display={'flex'} noPointer />
              <Text type={'text'} data-cy={'fuel-type'}>
                {fuelLabel}
              </Text>
            </SectionWrapper>
          )}
          {shouldShowTechnicalField(fieldsToShow, 'gearbox') && result.dataHubVehicle.gearbox && (
            <SectionWrapper isCompact={isCompact}>
              <Icon IconComponent={GearboxIcon} height={16} width={16} display={'flex'} noPointer />
              <WithTooltip title={gearbox ?? ''}>
                <Label isCompact={isCompact}>
                  <Text type={'text'} data-cy={GEARBOX_TYPE} ellipsis noWrap>
                    {gearbox}
                  </Text>
                </Label>
              </WithTooltip>
            </SectionWrapper>
          )}
        </>
      )}
    </>
  );
};

const GearboxEnergyField = ({
  result,
  isCompact,
  fieldsToShow,
}: {
  result: VehicleDetail;
  isCompact?: boolean;
  fieldsToShow?: TechnicalFieldKey[];
}) => {
  const gearboxCode = result.iamVehicle && result.iamVehicle.gearboxCode && `[${result.iamVehicle.gearboxCode}]`;
  const fuelLabel = formatFuelLabel(result.iamVehicle?.energy);

  return (
    <>
      {shouldShowTechnicalField(fieldsToShow, 'fuel') && result.iamVehicle && (
        <SectionWrapper isCompact={isCompact}>
          <Icon IconComponent={GasIcon} height={16} width={16} display={'flex'} noPointer />
          <Text type={'text'} data-cy={'energy-type'}>
            {fuelLabel}
          </Text>
        </SectionWrapper>
      )}
      {shouldShowTechnicalField(fieldsToShow, 'gearbox') &&
        result.iamVehicle &&
        (result.iamVehicle.gearbox || result.iamVehicle.gearboxType || result.iamVehicle.gearboxCode) && (
          <SectionWrapper isCompact={isCompact}>
            <Icon IconComponent={GearboxIcon} height={16} width={16} display={'flex'} noPointer />
            <Text type={'text'} data-cy={GEARBOX_TYPE} ellipsis noWrap>{`${
              result.iamVehicle.gearboxType ? capitalize(result.iamVehicle.gearboxType.toLowerCase()) : ''
            }${result.iamVehicle.gearboxType && result.iamVehicle.gearbox ? ' ' : ''}${
              result.iamVehicle.gearbox ? capitalize(result.iamVehicle.gearbox.toLowerCase()) : ''
            }${
              (result.iamVehicle.gearboxType || result.iamVehicle.gearbox) && result.iamVehicle.gearboxCode ? ' ' : ''
            }${result.iamVehicle.gearboxCode ? gearboxCode : ''}`}</Text>
          </SectionWrapper>
        )}
    </>
  );
};

const WheelBaseLengthField = ({ result, isCompact }: { result: VehicleDetail; isCompact?: boolean }) => {
  const dispatch = useDispatch();
  const technicalCriteria = getData(useSelector(getLastTechnicalCriteria));
  const hasTechnicalCriteriaGroups = (technicalCriteria?.groups?.size ?? 0) > 0;
  const wheelbaseLengthFromCriteria = Array.from(technicalCriteria?.groups?.values() ?? [])
    .flat()
    .find((info) => info.code === UTILITY_VEHICLE_WHEELBASE_TECHNICAL_CRITERIA_CODE)?.description;
  const wheelbaseLength = result?.dataHubVehicle
    ? result.dataHubVehicle.wheelbaseLength || wheelbaseLengthFromCriteria
    : result?.iamVehicle?.wheelbaseLength;

  useEffect(() => {
    if (result?.dataHubVehicle && !result.dataHubVehicle.wheelbaseLength && !hasTechnicalCriteriaGroups) {
      dispatch(fetchVehicleTechnicalCriteriaRequestSaga());
    }
  }, [dispatch, hasTechnicalCriteriaGroups, result]);

  return (
    wheelbaseLength && (
      <SectionWrapper isCompact={isCompact}>
        <Icon IconComponent={ArrowWidthIcon} height={16} width={16} display={'flex'} noPointer />
        <WithTooltip title={wheelbaseLength ?? ''}>
          <Label isCompact={isCompact}>
            <Text type={'text'} data-cy={GEARBOX_TYPE} ellipsis noWrap>
              {wheelbaseLength}
            </Text>
          </Label>
        </WithTooltip>
      </SectionWrapper>
    )
  );
};

const RoofHeightField = ({ result, isCompact }: { result: VehicleDetail; isCompact?: boolean }) => {
  const roofHeight = result?.dataHubVehicle?.roofHeight ?? result?.iamVehicle?.roofHeight;
  return (
    roofHeight && (
      <SectionWrapper isCompact={isCompact}>
        <Icon IconComponent={ArrowHeightIcon} height={16} width={16} display={'flex'} noPointer />
        <WithTooltip title={roofHeight ?? ''}>
          <Label isCompact={isCompact}>
            <Text type={'text'} data-cy={GEARBOX_TYPE} ellipsis noWrap>
              {roofHeight}
            </Text>
          </Label>
        </WithTooltip>
      </SectionWrapper>
    )
  );
};

const OtherField = ({
  result,
  hpPowerIAM,
  isCompact,
  fieldsToShow,
}: {
  result: VehicleDetail;
  hpPowerIAM: number | string | undefined;
  isCompact?: boolean;
  fieldsToShow?: TechnicalFieldKey[];
}) => {
  const { t } = useTranslation();
  const powerLabel = formatPowerLabel({
    kwValue: result.iamVehicle?.kwPower,
    hpValue: hpPowerIAM,
    t,
  });

  return (
    <>
      {result.iamVehicle && result.dataHubVehicle === undefined && (
        <>
          {shouldShowTechnicalField(fieldsToShow, 'engine') &&
            (result.iamVehicle.version || result.iamVehicle.engineCodes) && (
              <SectionWrapper isCompact={isCompact}>
                <Icon IconComponent={EngineIcon} height={16} width={16} display={'flex'} noPointer />
                <Text type={'text'} data-cy={'engine-code'}>
                  {`${result.iamVehicle.version ? result.iamVehicle.version : ''}
                ${result.iamVehicle.engineCodes ? ` [${result.iamVehicle.engineCodes}]` : ''}`}
                </Text>
              </SectionWrapper>
            )}
          {shouldShowTechnicalField(fieldsToShow, 'power') && (
            <SectionWrapper isCompact={isCompact}>
              <Icon IconComponent={BoltIcon} height={16} width={16} display={'flex'} noPointer />
              <Text type={'text'} data-cy={'power-type'}>
                {powerLabel}
              </Text>
            </SectionWrapper>
          )}
          {shouldShowTechnicalField(fieldsToShow, 'fuel') && (
            <GearboxEnergyField result={result} isCompact={isCompact} fieldsToShow={['fuel']} />
          )}
          {shouldShowTechnicalField(fieldsToShow, 'gearbox') && (
            <GearboxEnergyField result={result} isCompact={isCompact} fieldsToShow={['gearbox']} />
          )}
        </>
      )}
    </>
  );
};

const VehicleStatus = ({ result }: { result: VehicleDetail }) => {
  const { t } = useTranslation();
  const criteria = useGetCriteria();
  const simplifiedApplicability = criteria.simplifiedApplicability;
  const editedTechnicalCriteria = criteria.items
    .map((itemElement) => itemElement.data)
    .flat()
    .some((x) => !x?.enabled);

  return (
    <>
      {(simplifiedApplicability || editedTechnicalCriteria) && (
        <Flex size={ZERO_FLEX_SIZE} align={'center'}>
          <MarginBox ml={8}>
            <WithTooltip
              title={
                simplifiedApplicability
                  ? t('catalog.parts.simplified_applicability', 'Simplified applicability')
                  : t('catalog.parts.modified_criteria', 'Modified criteria')
              }
            >
              <div>
                <Link to={`${getCatalogSourceUrl(result.catalogSource)}/${result.vehicleKey}${ROUTER_CATALOG_DETAILS}`}>
                  <Icon IconComponent={ApplicabilityIcon} width={32} height={32} />
                </Link>
              </div>
            </WithTooltip>
          </MarginBox>
        </Flex>
      )}
    </>
  );
};

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
// NOSONAR - legacy field-composition function kept intact to preserve existing behavior
export const VehicleTabContentBase = ({
  result,
  vehicleImage,
  isBMM,
  isPopover,
  isSticky = false,
  isShowFull = true,
}: {
  result: VehicleDetail;
  vehicleImage: string | undefined;
  isBMM: boolean;
  isPopover?: boolean;
  isSticky?: boolean;
  isShowFull?: boolean;
}) => {
  const { t } = useTranslation();
  const isSmall = useSmall();
  const hpPowerIAM = result.iamVehicle?.hpPower ?? getHpFromKw(result.iamVehicle?.kwPower);
  const hpPowerDHKWsub =
    result.dataHubVehicle && result.dataHubVehicle.powerKw && result.dataHubVehicle.powerKw.substring(0, 3);
  const hpPowerDHKW = isNaN(Number(hpPowerDHKWsub)) ? hpPowerDHKWsub : Number(hpPowerDHKWsub);
  const hpPowerDHHP = typeof hpPowerDHKW === 'number' && Math.floor(1.341 * hpPowerDHKW);
  const isIAM = result.catalogSource === 'IAM';
  const showPhase = !result.dataHubVehicle && result.iamVehicle && !isBMM && result.iamVehicle.phase;

  return (
    <Box>
      <Flex align={isSmall ? FLEX_START : 'center'} wrap={isSmall ? 'wrap' : 'nowrap'}>
        <Box width={isSticky && !isShowFull ? 72 : 120}>
          <CenterFlex>
            <CarDetailHeaderImage
              imageUrl={vehicleImage}
              catalogSource={result.catalogSource}
              isSmall={isSticky && !isShowFull}
            />
          </CenterFlex>
        </Box>
        <Flex
          direction={'column'}
          justify={'center'}
          minHeight={isSticky && !isShowFull ? 50 : 70}
          size={1}
          minWidth={isSmall ? '100%' : undefined}
        >
          {isPopover && (
            <>
              <Text type={'highlight'}>
                {[
                  formatVehicleBrand(result.vehicleBrand),
                  result.model,
                  showPhase && t('catalog.parts.banner.phase', 'Phase {{phase}}', { phase: result.iamVehicle?.phase }),
                ]
                  .filter(Boolean)
                  .join(' ')}
              </Text>
              <MarginBox mt={5} />
            </>
          )}
          <Flex align={'center'} wrap={'wrap'} gap={isSmall ? '8px 0' : '8px 24px'}>
            <VINField result={result} isBMM={isBMM} isCompact={isSmall} />
            <Flex wrap={'wrap'}>
              <IAMField result={result} isCompact={isSmall} />
            </Flex>
          </Flex>
          {isShowFull && !isIAM && (
            <Flex align={'center'} wrap={'wrap'} gap={isSmall ? '8px 0' : '8px 24px'}>
              <DHField
                result={result}
                isBMM={isBMM}
                hpPowerDHKW={hpPowerDHKW}
                hpPowerDHHP={hpPowerDHHP}
                isCompact={isSmall}
              />
            </Flex>
          )}
          {isShowFull && isIAM && (
            <Flex align={'center'} wrap={'wrap'} gap={isSmall ? '8px 0' : '8px 24px'}>
              <OtherField result={result} hpPowerIAM={hpPowerIAM} isCompact={isSmall} />
            </Flex>
          )}
        </Flex>
        <VehicleStatus result={result} />
      </Flex>
    </Box>
  );
};

export const CompactVehicleTabContentBase = ({
  result,
  vehicleImage,
  isBMM,
}: {
  result: VehicleDetail;
  vehicleImage: string | undefined;
  isBMM: boolean;
}) => {
  const hpPowerIAM = result.iamVehicle?.hpPower ?? getHpFromKw(result.iamVehicle?.kwPower);
  const hpPowerDHKWsub = result.dataHubVehicle?.powerKw?.substring(0, 3);
  const hpPowerDHKW = isNaN(Number(hpPowerDHKWsub)) ? hpPowerDHKWsub : Number(hpPowerDHKWsub);
  const hpPowerDHHP = typeof hpPowerDHKW === 'number' && Math.floor(1.341 * hpPowerDHKW);
  const isIAM = result.catalogSource === 'IAM';

  return (
    <Flex direction={'row'} background={theme.color.grey95} minHeight={100}>
      <Box width={90}>
        <CenterFlex>
          <CarDetailHeaderImage imageUrl={vehicleImage} catalogSource={result.catalogSource} isSmall={false} />
        </CenterFlex>
      </Box>
      <Flex direction={'column'} maxWidth={410}>
        <Flex direction={'row'} wrap={'wrap'} align={'center'} justify={FLEX_START}>
          <VINField result={result} isBMM={isBMM} isCompact />
          <IAMField result={result} isCompact />
        </Flex>
        <Flex direction={'row'} wrap={'wrap'} align={'center'} justify={FLEX_START}>
          {!isIAM && (
            <DHField result={result} isBMM={isBMM} hpPowerDHKW={hpPowerDHKW} hpPowerDHHP={hpPowerDHHP} isCompact />
          )}
          {isIAM && <OtherField result={result} hpPowerIAM={hpPowerIAM} isCompact />}
        </Flex>
      </Flex>
    </Flex>
  );
};

export const VehicleTabContent = ({
  result,
  vehicleImage,
  isBMM,
  query,
  setIsChangeVehicle,
  isSticky,
}: {
  result: VehicleDetail;
  vehicleImage: string | undefined;
  isBMM: boolean;
  query: string;
  setIsChangeVehicle?: (b: boolean) => void;
  isSticky?: boolean;
}) => {
  useResetScrollSharp();
  const isSmall = useSmall();
  const [isShowFull, setIsShowFull] = useState(!(isSticky || isSmall));

  useEffect(() => {
    setIsShowFull(!(isSticky || isSmall));
  }, [isSticky, isSmall]);

  const showExpandToggle = isSticky || isSmall;

  return (
    <SFlex background={theme.color.white} wrap={'wrap'}>
      <VehicleBanner
        isCompact={isSticky || isSmall}
        actions={
          <CatalogButtons
            query={query}
            setIsChangeVehicle={setIsChangeVehicle}
            showExpandToggle={showExpandToggle}
            isShowFull={isShowFull}
            setIsShowFull={setIsShowFull}
          />
        }
      >
        <VehicleTabContentBase
          result={result}
          vehicleImage={vehicleImage}
          isBMM={isBMM}
          isSticky={isSticky}
          isShowFull={isShowFull}
        />
      </VehicleBanner>
    </SFlex>
  );
};
